import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useTopUpStatus } from '../hooks/use-credits';
import { topUpOutcome } from '../utils/topup-outcome';
import { TopUpToneIcon } from './topup-tone-icon';

interface TopUpProgressProps {
  topUpId: string;
  onDone: () => void;
  onUseCheckout: () => void;
}

/** Follows a saved-card charge in place, since there is no page to come back from. */
export const TopUpProgress = ({ topUpId, onDone, onUseCheckout }: TopUpProgressProps) => {
  const { t } = useTranslation();
  const { topUp, isTimedOut } = useTopUpStatus(topUpId);
  const { tone, title, description, isDeclined } = topUpOutcome(
    t,
    topUp,
    isTimedOut,
    t('CHARGING_CARD', 'Charging your card...')
  );
  const isFinished = tone === 'success' || tone === 'failure';

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <TopUpToneIcon tone={tone} className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-medium text-foreground">{title}</p>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      {isDeclined && <Button onClick={onUseCheckout}>{t('USE_DIFFERENT_CARD', 'Use a different card')}</Button>}
      {isFinished && !isDeclined && (
        <Button variant="outline" onClick={onDone}>
          {tone === 'success' ? t('DONE', 'Done') : t('TRY_AGAIN', 'Try Again')}
        </Button>
      )}
    </div>
  );
};
